import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, RotateCcw, Download } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";

const uid = () => Math.random().toString(36).slice(2, 10);
const inputCls = "w-full bg-transparent border border-ink/15 rounded-md py-2 px-3 text-ink text-sm focus:outline-none focus:border-trailride transition-colors";

const isoToDate = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");
const dateToIso = (d) => (d ? new Date(d + "T23:59:59Z").toISOString() : null);

export function MembershipTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/membership-config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  if (!cfg) return <p className="font-sans text-ink/40">Loading…</p>;

  const setField = (k, v) => setCfg({ ...cfg, [k]: v });
  const updateTier = (i, patch) => setCfg({ ...cfg, donation_tiers: cfg.donation_tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t)) });
  const removeTier = (i) => setCfg({ ...cfg, donation_tiers: cfg.donation_tiers.filter((_, idx) => idx !== i) });
  const addTier = () => setCfg({ ...cfg, donation_tiers: [...cfg.donation_tiers, { id: uid(), amount: 5, title: "", description: "" }] });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/membership-config", cfg);
      toast.success("Membership settings saved");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const resetAll = async () => {
    if (!window.confirm("Expire ALL memberships for a new period? Every member will be marked unpaid and must renew & pay dues again.")) return;
    try {
      const res = await api.post("/admin/membership/reset");
      toast.success(`Reset ${res.data.reset} member(s). They'll need to renew.`);
    } catch { toast.error("Reset failed"); }
  };

  const exportCsv = () => { window.open(`${API}/admin/members/export`, "_blank"); };

  return (
    <div data-testid="admin-membership-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl font-bold text-ink">Membership & dues</h2>
          <p className="font-sans text-sm text-ink/55 mt-1">Set annual dues, the membership period, and the donation tiers shown at checkout.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} data-testid="membership-export" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-ink/20 font-sans text-sm font-medium text-ink hover:border-trailride hover:text-trailride"><Download size={15} /> Export CSV</button>
          <button onClick={save} disabled={saving} data-testid="membership-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-trailride text-white font-sans text-sm font-medium hover:bg-trailride-dark disabled:opacity-60"><Save size={16} /> {saving ? "Saving…" : "Save"}</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 border border-ink/10 rounded-lg p-5">
        <div>
          <label className="font-sans text-xs text-ink/60">Annual dues (USD)</label>
          <input type="number" min="0" step="1" value={cfg.dues_amount} onChange={(e) => setField("dues_amount", parseFloat(e.target.value) || 0)} data-testid="dues-amount-input" className={inputCls} />
        </div>
        <div>
          <label className="font-sans text-xs text-ink/60">Membership period label</label>
          <input value={cfg.period_label} onChange={(e) => setField("period_label", e.target.value)} placeholder="2025–2026" data-testid="period-label-input" className={inputCls} />
        </div>
        <div>
          <label className="font-sans text-xs text-ink/60">Membership expires</label>
          <input type="date" value={isoToDate(cfg.expires_at)} onChange={(e) => setField("expires_at", dateToIso(e.target.value))} data-testid="expires-at-input" className={inputCls} />
        </div>
      </div>

      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-lg font-bold text-ink">Donation tiers</h3>
          <button onClick={addTier} data-testid="add-tier" className="inline-flex items-center gap-1.5 font-sans text-xs font-medium border border-ink/20 rounded-full px-3 py-1.5 text-ink hover:border-trailride hover:text-trailride"><Plus size={13} /> Add tier</button>
        </div>
        <div className="space-y-3">
          {cfg.donation_tiers.length === 0 && <p className="font-sans text-sm text-ink/40">No tiers. Members can still enter a custom amount.</p>}
          {cfg.donation_tiers.map((t, i) => (
            <div key={t.id || i} className="grid grid-cols-1 md:grid-cols-12 gap-3 border border-ink/15 rounded-md p-3 items-start" data-testid={`tier-row-${i}`}>
              <div className="md:col-span-2">
                <label className="font-sans text-xs text-ink/50">Amount $</label>
                <input type="number" min="1" step="1" value={t.amount} onChange={(e) => updateTier(i, { amount: parseFloat(e.target.value) || 0 })} data-testid={`tier-amount-${i}`} className={inputCls} />
              </div>
              <div className="md:col-span-3">
                <label className="font-sans text-xs text-ink/50">Title</label>
                <input value={t.title} onChange={(e) => updateTier(i, { title: e.target.value })} data-testid={`tier-title-${i}`} className={inputCls} />
              </div>
              <div className="md:col-span-6">
                <label className="font-sans text-xs text-ink/50">What it supports</label>
                <input value={t.description} onChange={(e) => updateTier(i, { description: e.target.value })} data-testid={`tier-desc-${i}`} className={inputCls} />
              </div>
              <div className="md:col-span-1 flex md:justify-end md:pt-6">
                <button onClick={() => removeTier(i)} data-testid={`tier-remove-${i}`} className="p-2 rounded-md hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-amber-300 bg-amber-50 rounded-lg p-5">
        <h3 className="font-serif text-lg font-bold text-ink">New membership period</h3>
        <p className="font-sans text-sm text-ink/70 mt-1 mb-4">Expire all current memberships so everyone renews and pays dues again. Set the new expiration date above and save first.</p>
        <button onClick={resetAll} data-testid="membership-reset" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-white font-sans text-sm font-medium hover:bg-ink/85"><RotateCcw size={15} /> Reset & expire all memberships</button>
      </div>
    </div>
  );
}
